import { registerBlockType } from "@wordpress/blocks";
import {
    InspectorControls,
    useBlockProps,
    BlockControls,
} from "@wordpress/block-editor";
import {
    PanelBody,
    __experimentalView as View,
    __experimentalText as Text,
    Button,
    ToolbarGroup,
    ToolbarButton,
} from "@wordpress/components";
import { useSelect } from "@wordpress/data";
import UnitRange from "./components/UnitRange";
import { useEffect, useState } from "@wordpress/element";
import { useEntityProp } from "@wordpress/core-data";

registerBlockType("danone/featured-image-url", {
    apiVersion: 2,
    title: "Featured Image from URL",
    icon: "format-image",
    category: "media",
    attributes: {
        objectFit: {
            type: "string",
            default: "cover",
        },
        alignment: {
            type: "string",
            default: "center",
        },
        height: {
            type: "object",
            default: { value: 300, unit: "px" },
        },
        width: {
            type: "object",
            default: { value: 100, unit: "%" },
        },
        enableLazyLoad: {
            type: "boolean",
            default: true,
        },
        enableLink: {
            type: "boolean",
            default: false,
        },
    },
    usesContext: ["postId", "postType", "queryId"],

    edit: function Edit({ attributes, setAttributes, context }) {
        const blockProps = useBlockProps();
        const {
            objectFit,
            height,
            width,
            enableLazyLoad,
            alignment,
            enableLink,
        } = attributes;
        const [imageUrl, setImageUrl] = useState("");
        const [currentPostType, setCurrentPostType] = useState("");

        const currentPostId = useSelect(
            (select) => {
                const { getCurrentPostId, getEditedPostAttribute } =
                    select("core/editor");
                const { getSelectedBlock, getBlockParents } =
                    select("core/block-editor");

                // Jeśli jesteśmy w Query Loop
                if (context.queryId) {
                    return context["postId"];
                }

                // Jeśli jesteśmy poza Query Loop
                return getCurrentPostId();
            },
            [context]
        );

        useEffect(() => {
            if (currentPostId) {
                const { select } = wp.data;
                const postType = select("core").getEditedEntityRecord(
                    "postType",
                    "products",
                    currentPostId
                )?.type;

                if (postType === "products") {
                    const meta = select("core").getEditedEntityRecord(
                        "postType",
                        "products",
                        currentPostId
                    )?.meta;

                    setImageUrl(meta?.featured_image_url || "");
                    setCurrentPostType("products");
                }
            }
        }, [currentPostId]);

        // Sprawdź, czy blok może być używany dla danego typu postu
        const isValidPostType = currentPostType === "products";

        if (!isValidPostType) {
            return (
                <div {...blockProps}>
                    <div
                        style={{
                            padding: "20px",
                            backgroundColor: "#f8d7da",
                            color: "#721c24",
                            borderRadius: "4px",
                            textAlign: "center",
                        }}
                    >
                        Ten blok jest dostępny tylko dla produktów.
                    </div>
                </div>
            );
        }

        const OBJECT_FIT_OPTIONS = [
            { label: "Cover", value: "cover" },
            { label: "Contain", value: "contain" },
            { label: "Fill", value: "fill" },
            { label: "None", value: "none" },
        ];

        return (
            <>
                <BlockControls>
                    <ToolbarGroup>
                        <ToolbarButton
                            icon="align-left"
                            title="Wyrównaj do lewej"
                            isActive={alignment === "left"}
                            onClick={() => setAttributes({ alignment: "left" })}
                        />
                        <ToolbarButton
                            icon="align-center"
                            title="Wyśrodkuj"
                            isActive={alignment === "center"}
                            onClick={() =>
                                setAttributes({ alignment: "center" })
                            }
                        />
                        <ToolbarButton
                            icon="align-right"
                            title="Wyrównaj do prawej"
                            isActive={alignment === "right"}
                            onClick={() =>
                                setAttributes({ alignment: "right" })
                            }
                        />
                    </ToolbarGroup>
                </BlockControls>

                <InspectorControls>
                    <PanelBody title="Image Settings" initialOpen={true}>
                        <View style={{ padding: "15px 0" }}>
                            <View style={{ marginBottom: "20px" }}>
                                <Text
                                    as="h4"
                                    style={{
                                        margin: "0 0 8px 0",
                                        padding: "0",
                                        fontSize: "13px",
                                        fontWeight: "500",
                                    }}
                                >
                                    Object Fit
                                </Text>
                                <div
                                    style={{
                                        display: "flex",
                                        gap: "8px",
                                        flexWrap: "wrap",
                                    }}
                                >
                                    {OBJECT_FIT_OPTIONS.map((option) => (
                                        <Button
                                            key={option.value}
                                            variant={
                                                objectFit === option.value
                                                    ? "primary"
                                                    : "secondary"
                                            }
                                            onClick={() =>
                                                setAttributes({
                                                    objectFit: option.value,
                                                })
                                            }
                                            style={{
                                                margin: "0",
                                                flexGrow: "1",
                                                justifyContent: "center",
                                            }}
                                        >
                                            {option.label}
                                        </Button>
                                    ))}
                                </div>
                            </View>

                            <UnitRange
                                label="Width"
                                rangeValue={width.value}
                                onRangeChange={(value) =>
                                    setAttributes({
                                        width: { ...width, value },
                                    })
                                }
                                unitValue={width}
                                onUnitChange={(value) =>
                                    setAttributes({ width: value })
                                }
                                min={1}
                                max={width.unit === "%" ? 100 : 2000}
                                help="Set image width"
                                style={{ marginBottom: "20px" }}
                            />

                            <UnitRange
                                label="Height"
                                rangeValue={height.value}
                                onRangeChange={(value) =>
                                    setAttributes({
                                        height: { ...height, value },
                                    })
                                }
                                unitValue={height}
                                onUnitChange={(value) =>
                                    setAttributes({ height: value })
                                }
                                min={1}
                                max={height.unit === "%" ? 100 : 2000}
                                help="Set image height"
                                style={{ marginBottom: "20px" }}
                            />

                            <View style={{ marginBottom: "20px" }}>
                                <Button
                                    variant={
                                        enableLazyLoad ? "primary" : "secondary"
                                    }
                                    onClick={() =>
                                        setAttributes({
                                            enableLazyLoad: !enableLazyLoad,
                                        })
                                    }
                                    style={{
                                        width: "100%",
                                        justifyContent: "center",
                                    }}
                                >
                                    {enableLazyLoad
                                        ? "Lazy Loading Enabled"
                                        : "Lazy Loading Disabled"}
                                </Button>
                            </View>

                            <View style={{ marginBottom: "20px" }}>
                                <Button
                                    variant={
                                        enableLink ? "primary" : "secondary"
                                    }
                                    onClick={() =>
                                        setAttributes({
                                            enableLink: !enableLink,
                                        })
                                    }
                                    style={{
                                        width: "100%",
                                        justifyContent: "center",
                                    }}
                                >
                                    {enableLink
                                        ? "Link to Product Enabled"
                                        : "Link to Product Disabled"}
                                </Button>
                            </View>

                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setAttributes({
                                        objectFit: "cover",
                                        height: { value: 300, unit: "px" },
                                        width: { value: 100, unit: "%" },
                                        enableLazyLoad: true,
                                    });
                                }}
                                style={{
                                    width: "100%",
                                    marginTop: "10px",
                                }}
                            >
                                Reset to Default Settings
                            </Button>
                        </View>
                    </PanelBody>
                </InspectorControls>

                <div {...blockProps} style={{ textAlign: alignment }}>
                    {imageUrl ? (
                        <div
                            style={{
                                cursor: enableLink ? "pointer" : "default",
                            }}
                        >
                            <img
                                src={imageUrl}
                                alt=""
                                style={{
                                    width: `${width.value}${width.unit}`,
                                    height: `${height.value}${height.unit}`,
                                    objectFit: objectFit,
                                    display: "block",
                                    margin:
                                        alignment === "center"
                                            ? "auto"
                                            : alignment === "right"
                                            ? "0 0 0 auto"
                                            : "0 auto 0 0",
                                }}
                                loading={enableLazyLoad ? "lazy" : "eager"}
                            />
                        </div>
                    ) : (
                        <div
                            style={{
                                width: `${width.value}${width.unit}`,
                                height: `${height.value}${height.unit}`,
                                backgroundColor: "#f0f0f0",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#666",
                                fontSize: "14px",
                                margin:
                                    alignment === "center"
                                        ? "auto"
                                        : alignment === "right"
                                        ? "0 0 0 auto"
                                        : "0 auto 0 0",
                            }}
                        >
                            No featured image URL set
                        </div>
                    )}
                </div>
            </>
        );
    },

    save: function Save({ attributes }) {
        const blockProps = useBlockProps.save();
        const {
            objectFit,
            height,
            width,
            enableLazyLoad,
            alignment,
            enableLink,
        } = attributes;

        return (
            <div {...blockProps} style={{ textAlign: alignment }}>
                <div
                    className="featured-image-url-dynamic"
                    data-object-fit={objectFit}
                    data-width={`${width.value}${width.unit}`}
                    data-height={`${height.value}${height.unit}`}
                    data-lazy-load={enableLazyLoad}
                    data-alignment={alignment}
                    data-enable-link={enableLink}
                />
            </div>
        );
    },
});
